import React, { useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  LocalHospital as LogoIcon,
  EmailOutlined,
} from '@mui/icons-material';
import { isTestUi } from '../common/TestingEnvBadge';
import { trackCtaClick } from '../../utils/analytics';

const COMPANY_EMAIL = 'sales@carehivehub.com';

const NAV_LINKS = [
  { to: '/features', label: 'Features' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/security', label: 'Security' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export const Logo: React.FC<{ className?: string }> = ({ className }) => (
  <Link to="/" className={`flex items-center gap-2 ${className || ''}`}>
    <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center flex-shrink-0">
      <LogoIcon className="text-white" style={{ fontSize: 20 }} />
    </span>
    <span className="text-xl font-bold text-gray-900">CarehiveHub</span>
  </Link>
);

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `text-sm font-medium transition-colors duration-150 ${
    isActive ? 'text-blue-700' : 'text-gray-600 hover:text-blue-700'
  }`;

const MarketingHeader: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className={`sticky z-40 bg-white/90 backdrop-blur border-b border-gray-100 ${isTestUi() ? 'top-11' : 'top-0'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Logo />

          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} className={navLinkClass}>
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                trackCtaClick('header', 'login');
                navigate('/login');
              }}
              className="text-sm font-medium text-gray-700 hover:text-blue-700 px-3 py-2 transition-colors duration-150"
            >
              Log In
            </button>
            <button
              type="button"
              onClick={() => {
                trackCtaClick('header', 'request-demo');
                navigate('/request-demo');
              }}
              className="btn-primary text-sm"
            >
              Request a Demo
            </button>
          </div>

          <button
            type="button"
            aria-label="Toggle navigation menu"
            className="md:hidden p-2 text-gray-700"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white">
          <div className="px-4 py-4 space-y-3">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className="block text-base font-medium text-gray-700 hover:text-blue-700"
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </NavLink>
            ))}
            <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  trackCtaClick('mobile_menu', 'login');
                  setMenuOpen(false);
                  navigate('/login');
                }}
                className="btn-secondary w-full"
              >
                Log In
              </button>
              <button
                type="button"
                onClick={() => {
                  trackCtaClick('mobile_menu', 'request-demo');
                  setMenuOpen(false);
                  navigate('/request-demo');
                }}
                className="btn-primary w-full"
              >
                Request a Demo
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const FOOTER_LINKS = [
  {
    heading: 'Product',
    links: [
      { to: '/features', label: 'Features' },
      { to: '/pricing', label: 'Pricing' },
      { to: '/security', label: 'Security & HIPAA' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { to: '/about', label: 'About' },
      { to: '/contact', label: 'Contact' },
      { to: '/request-demo', label: 'Request a Demo' },
      { to: '/login', label: 'Log In' },
    ],
  },
];

const MarketingFooter: React.FC = () => (
  <footer className="bg-gray-900 text-gray-300">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <div>
          <Logo className="[&_span:last-child]:text-white" />
          <p className="mt-4 text-sm text-gray-400 max-w-sm">
            AI-powered care management built for the people who provide care. CarehiveHub helps
            Adult Family Homes, assisted living communities, and residential care providers
            reduce paperwork and deliver better care.
          </p>
        </div>

        {FOOTER_LINKS.map((column) => (
          <div key={column.heading}>
            <h3 className="text-sm font-semibold text-white mb-3">{column.heading}</h3>
            <ul className="space-y-2">
              {column.links.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-gray-400 hover:text-white transition-colors duration-150">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div>
          <h3 className="text-sm font-semibold text-white mb-3">Contact</h3>
          <address className="not-italic space-y-3 text-sm text-gray-400">
            <p>
              <a
                href={`mailto:${COMPANY_EMAIL}`}
                className="inline-flex items-center gap-2 hover:text-white transition-colors duration-150"
              >
                <EmailOutlined className="text-gray-500 flex-shrink-0" style={{ fontSize: 18 }} />
                {COMPANY_EMAIL}
              </a>
            </p>
          </address>
        </div>
      </div>

      <div className="mt-10 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-gray-500">
          &copy; {new Date().getFullYear()} CarehiveHub. All rights reserved.
        </p>
        <p className="text-xs text-gray-500">Built for HIPAA-conscious care teams.</p>
      </div>
    </div>
  </footer>
);

const MarketingLayout: React.FC = () => (
  <div className="min-h-screen flex flex-col bg-white">
    <MarketingHeader />
    <main className="flex-1">
      <Outlet />
    </main>
    <MarketingFooter />
  </div>
);

export default MarketingLayout;
